import ArrowIcon from 'components/common/ArrowIcon';
import SectionContainer from 'components/common/SectionContainer';
import { supportPath } from 'constants/help-center';
import useLocaleLink from 'hooks/useLocaleLink';
import { useTranslations } from 'next-intl';
import type { FAQCategory } from 'types/help-center';
import hotGif from '@/public/images/icons/hot.gif';

interface FAQSectionProps {
  categories: FAQCategory[];
}

function FAQCard({ category }: { category: FAQCategory }) {
  const t = useTranslations('support');
  const localeLink = useLocaleLink();

  return (
    <div className="flex flex-col gap-3 overflow-hidden rounded-2xl border border-base-line-primary p-4 backdrop-blur-sm dark:border-transparent dark:bg-base-hover-fill-primary pad:gap-4 pad:p-6">
      <div className="flex items-center gap-2">
        {category.showRanking && (
          <img src={typeof hotGif === 'string' ? hotGif : hotGif.src} alt="" className="-mt-1 size-5 pad:size-6" />
        )}
        <a
          href={`${localeLink}${category.href}`}
          className="text-[18px] font-medium text-base-label transition-colors hover:text-base-primary pad:text-xl"
        >
          {category.title}
        </a>
      </div>

      <div className="flex flex-col">
        {category.articles.map((article, idx) => (
          <a
            key={article.id}
            href={`${localeLink}${article.href}`}
            className="group/article -mx-2 flex items-center gap-4 rounded-md px-2 py-2 transition-colors hover:bg-base-fill-secondary pad:h-14 pad:py-0"
          >
            {category.showRanking && <span className="text-md font-medium text-base-primary">{idx + 1}</span>}
            <span className="line-clamp-1 text-md text-base-label transition-colors group-hover/article:text-base-primary">
              {article.title}
            </span>
          </a>
        ))}
      </div>

      {!category.showRanking && (
        <a
          href={`${localeLink}${category.href}`}
          className="inline-flex items-center gap-1 text-sm font-medium text-base-primary transition-opacity hover:opacity-80"
        >
          {t('seeAllArticles', { count: category.totalCount })}
          <ArrowIcon className="text-base-primary" />
        </a>
      )}
    </div>
  );
}

export default function FAQSection({ categories }: FAQSectionProps) {
  const t = useTranslations('support');
  const localeLink = useLocaleLink();
  const leftColumn = categories.filter((_, i) => i % 2 === 0);
  const rightColumn = categories.filter((_, i) => i % 2 !== 0);

  return (
    <SectionContainer>
      <div className="mb-6 flex items-center justify-between pad:mb-10">
        <h2 className="text-[22px] font-bold text-base-label pad:text-5xl">{t('faqTitle')}</h2>
        <a
          href={`${localeLink}${supportPath('/FAQ')}`}
          className="flex items-center gap-1 text-sm font-medium text-base-primary transition-opacity hover:opacity-80 pad:text-md"
        >
          {t('more')}
          <ArrowIcon className="size-6 text-base-primary" />
        </a>
      </div>
      <div className="flex flex-col gap-4 pc-small:flex-row pc-small:gap-6">
        <div className="flex flex-1 flex-col gap-4 pc-small:gap-6">
          {leftColumn.map((cat) => (
            <FAQCard key={cat.title} category={cat} />
          ))}
        </div>
        <div className="flex flex-1 flex-col gap-4 pc-small:gap-6">
          {rightColumn.map((cat) => (
            <FAQCard key={cat.title} category={cat} />
          ))}
        </div>
      </div>
    </SectionContainer>
  );
}
